<template>
  <div class="page event-single">
    <ClientOnly>
      <OrganismsBWTextOverlay :organism="brandWindow" />
    </ClientOnly>
    <div
      class="max-w-7xl py-16 px-8 gap-16 mx-auto grid grid-cols-1 lg:grid-cols-[2fr_1fr]"
    >
      <article class="">
        <AtomsCollectionButton
          class="px-0"
          :collection="event?.eventDetails?.collection"
          disable-shadow
        />
        <ClientOnly>
          <h2
            id="event-subtitle"
            class="mb-4 text-4xl font-semibold sm:text-5xl font-archer"
          ></h2>
          <h3
            class="event-date font-arial flex items-center text-sm text-black font-bold mb-4 before:content-[url('/img/heroicon-calendar.svg')] before:inline-block before:w-6 before:aspect-square before:mr-1"
          >
            {{ eventDateFormatted }}
          </h3>
          <div
            id="event-content"
            class="mb-8 content"
            v-html="event.content"
          ></div>
          <MoleculesSocialShareLinks :molecule="socialShare" />
        </ClientOnly>
      </article>
      <aside class="relative max-w-2xl mx-auto">
        <div
          class="p-4 mb-12 border border-neutral-400 shadow-[0_10px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_3px_rgba(0,0,0,0.07)]"
        >
          <div class="drop-shadow-[-1px_1px_0_#6b225c]">
            <h2
              id="add-to-calendar-heading"
              class="px-4 pt-5 mb-2 text-xl font-bold text-center text-white bg-bc-dark-blue pb-11"
            >
              Add to Your Calendar
            </h2>
          </div>
          <button
            class="flex items-center text-white w-full py-2 px-4 rounded bg-bc-dark-purple gap-8 drop-shadow-[-1px_1px_0_#387592]"
            @click="addToCalendar"
          >
            <div class="font-bold text-center">
              <div class="text-lg">{{ startDateTimeFormatted.mmm }}</div>
              <div class="text-4xl">{{ startDateTimeFormatted.d }}</div>
            </div>
            <div class="w-full text-left">
              <div>
                <strong>{{ event?.eventDetails?.location }}</strong>
              </div>
              <div>
                {{ startDateTimeFormatted.time }} -
                {{ endDateTimeFormatted.time }}
              </div>
            </div>
          </button>
        </div>
        <div
          v-if="
            event?.eventDetails?.neighborhood?.length
              ? event?.eventDetails?.neighborhood[0]
              : false
          "
          class="text-gray-800 border shadow-[0_10px_8px_rgba(0,0,0,0.04)] background-white border-neutral-400 event-neighborhood hover:shadow-[0_4px_3px_rgba(0,0,0,0.07)]"
        >
          <img
            class="w-full aspect-[12/7] object-cover mb-4"
            :src="
              getImageSrc(
                event?.eventDetails?.neighborhood[0]?.neighborhood?.thumbnail,
                'medium',
              )
            "
            :alt="
              event?.eventDetails?.neighborhood?.length
                ? event?.eventDetails?.neighborhood[0]?.thumbnail?.altText
                : false
            "
          />
          <div class="px-4">
            <h2 class="mb-3 text-2xl font-bold leading-none title font-archer">
              {{ event?.eventDetails?.neighborhood[0]?.title }}
            </h2>
            <p
              class="mb-2 text-sm font-bold text-gray-500 uppercase subtitle font-arial"
            >
              {{
                getNeighborhoodNavSubtitle(
                  event?.eventDetails?.neighborhood[0]?.types,
                  getRoundedPrice(
                    event?.eventDetails?.neighborhood[0]?.basePrice,
                  ),
                  event?.eventDetails?.neighborhood[0]?.neighborhood?.status,
                  true,
                )
              }}
            </p>
            <div class="text-sm font-bold text-bc-wc-green subtitle font-arial">
              In
              {{ event?.eventDetails?.neighborhood[0]?.neighborhood?.city }},
              {{ event?.eventDetails?.neighborhood[0]?.neighborhood?.state }}
            </div>
            <AtomsCollectionButton
              class="px-0"
              :collection="event?.eventDetails?.collection"
              disable-shadow
            />
          </div>
          <NuxtLink
            class="uppercase font-medium bg-bc-blue block text-center text-white font-ubuntu text-lg py-[11px] after:content-['\00bb'] after:ml-1 px-1"
            :to="event?.eventDetails?.neighborhood[0]?.uri"
            >Explore This Neighborhood</NuxtLink
          >
        </div>
      </aside>
    </div>
    <OrganismsTextCTA
      class="pt-[60px] pb-12 mx-auto max-w-screen-xl my-20"
      :organism="beTheFirst"
    />
    <OrganismsModalForm>
      <header class="mb-4">
        <h2 class="mb-4 text-4xl font-semibold text-black font-archer">
          Be the First
        </h2>
        <p v-if="!isFormSubmitted" class="text-lg text-gray-500">
          Please provide us your information and we will get back to you.
        </p>
      </header>
      <FormsHubSpotForm
        :form="{ id: '767c334f-0893-4757-8fa6-fd4b253bd691' }"
        @submit="isFormSubmitted = true"
      />
    </OrganismsModalForm>
    <MoleculesLoadingOverlay v-if="loading" />
  </div>
</template>

<style>
.event-single {
  .brand-window-text-overlay .content {
    @apply w-full;
    .atom-collection-button {
      @apply absolute top-1 left-1 sm:top-4 sm:left-4 md:top-6 md:left-6 lg:top-8 lg:left-8 xl:top-12 xl:left-12;
    }
  }
  article .content {
    p {
      @apply mb-4;
    }
    a {
      @apply hover:underline text-bc-blue;
    }
  }
  #add-to-calendar-heading {
    clip-path: polygon(
      0 0,
      100% 0,
      100% calc(100% - 25px),
      calc(50% - 25px) calc(100% - 25px),
      50% 100%,
      calc(50% + 25px) calc(100% - 25px),
      0 calc(100% - 25px)
    );
  }

  .image img {
    @apply shadow-lg rounded max-w-[min(72rem,_100%)];
  }
}
</style>

<script setup>
const route = useRoute();
import { useAsyncQuery } from "#imports";
import { GET_EVENT } from "~/graphql/GetEvent";
import { storeToRefs } from "pinia";
import { atcb_action, atcb_init } from "add-to-calendar-button";
import "add-to-calendar-button/assets/css/atcb.css";
import { format } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";
const modalForm = useModalForm();
const isFormSubmitted = ref(false);

const breadcrumbs = useBreadcrumbs();
const { breadcrumbsList } = storeToRefs(breadcrumbs);
// Explicit, stable key (from route.params, not route.path) so the SSR payload
// is reused on hydration instead of refetching on this dynamic route. See
// homes/[community] index.vue for the full story.
const { data, pending, error } = await useAsyncQuery({
  query: GET_EVENT,
  variables: { uri: route.path },
  key: `event:${route.params.event}`,
});

const event = computed(() => {
  return data?.value?.event ?? [];
});

const loading = pending;

const computedBreadcrumbs = computed(() => {
  return [
    { title: "About Us", link: "/about-us/our-team/" },
    { title: "Events", link: "/events/" },
    { title: event?.value?.title },
  ];
});

watch(computedBreadcrumbs, () => {
  breadcrumbsList.value = computedBreadcrumbs.value;
});

const brandWindow = computed(() => {
  return {
    backgroundImage: event?.value?.eventDetails?.image,
    brandWindowLayout: "text-overlay-center",
    containerWidth: "full-width",
    title1: event?.value?.title,
    collection: event?.value?.eventDetails?.collection,
  };
});

const eventDateFormatted = computed(() =>
  formatEventDateRange(
    event?.value?.eventDetails?.startDateTime,
    event?.value?.eventDetails?.endDateTime,
  ),
);

const startDateTimeFormatted = computed(() => {
  if (!event?.value?.eventDetails?.startDateTime) {
    return "";
  }
  const eventStartDateTime = zonedTimeToUtc(
    event?.value?.eventDetails?.startDateTime,
    "America/Denver",
  );
  const dateObject = new Date(eventStartDateTime);
  return {
    full: format(dateObject, "MMM d, yyyy"),
    mmm: format(dateObject, "MMM"),
    d: format(dateObject, "d"),
    yyyy: format(dateObject, "yyyy"),
    time: format(dateObject, "H:mmbbb"),
  };
});

const endDateTimeFormatted = computed(() => {
  if (!event?.value?.eventDetails?.endDateTime) {
    return "";
  }
  const eventEndDateTime = zonedTimeToUtc(
    event?.value?.eventDetails?.endDateTime,
    "America/Denver",
  );
  const dateObject = new Date(eventEndDateTime);
  return {
    full: format(dateObject, "MMM d, yyyy"),
    mmm: format(dateObject, "MMM"),
    d: format(dateObject, "d"),
    yyyy: format(dateObject, "yyyy"),
    time: format(dateObject, "H:mmbbb"),
  };
});

const calendarConfig = computed(() => {
  if (!event?.value?.eventDetails?.startDateTime) {
    return {};
  }
  const eventStartDateTime = zonedTimeToUtc(
    event?.value?.eventDetails?.startDateTime,
    "America/Denver",
  );
  const startDateObject = new Date(eventStartDateTime);
  const eventStartDate = format(startDateObject, "yyyy-M-d");
  const eventStartTime = format(startDateObject, "HH:mm");

  const eventEndDateTime = zonedTimeToUtc(
    event?.value?.eventDetails?.endDateTime,
    "America/Denver",
  );
  const endDateObject = new Date(eventEndDateTime);
  const eventEndDate = format(endDateObject, "yyyy-M-d");
  const eventEndTime = format(endDateObject, "HH:mm");

  const directionsListItem = event?.value?.eventDetails?.directionsUrl
    ? "<br>→ [url]" + event?.value?.eventDetails?.directionsUrl + "[/url]"
    : "";

  const locationListItem = event?.value?.eventDetails?.location
    ? "<br>→ " + event?.value?.eventDetails?.location
    : "";

  const location =
    directionsListItem || locationListItem
      ? "<br>Location:" + locationListItem + directionsListItem + "<br>"
      : "";

  const descriptionLink =
    "<br>Event Details:<br>→ [url]https://livebouldercreek.com" +
    event?.value?.uri +
    "[/url]<br>";

  return {
    name: event?.value?.title,
    description: event?.value?.content + location + descriptionLink,
    startDate: eventStartDate,
    endDate: eventEndDate,
    startTime: eventStartTime,
    endTime: eventEndTime,
    options: [
      "Apple",
      "Google",
      "iCal",
      "Microsoft365",
      "Outlook.com",
      "Yahoo",
    ],
    timeZone: "America/Denver",
    location: event?.value?.eventDetails?.directionsUrl,
    trigger: "click",
    iCalFileName: event?.value?.title?.replace(/ /g, "_"),
  };
});

const addToCalendar = (event) => {
  atcb_action(calendarConfig.value, event.target);
};

const socialShare = {
  shareUrl:
    "https://livebouldercreek.com/events/lanterns-at-rock-creek-model-opening/",
  title:
    event?.value?.title ?? "Check Out this Boulder Creek Neighboroods Event!",
  imgSrc:
    "https://livebouldercreek.com/wp-content/uploads/2021/05/BW-Neighborhood-RF_wee_1.jpg",
};

const beTheFirst = {
  title1: "Be the First",
  description:
    "<p>Be the first to know about our Colorado neighborhoods and homes, including early access to pricing, new home plans and VIP events.</p>",
  button1: true,
  button1Text: "Get Updates",
  link1: "#modal-form",
};

onUpdated(() => {
  nextTick(() => {
    const eventSubtitle = document.getElementById("event-subtitle");
    const eventContent = document.getElementById("event-content");

    if (eventSubtitle && eventContent) {
      const firstParagraph = eventContent.querySelector("p:first-of-type");
      if (firstParagraph) {
        firstParagraph.style.display = "none";
        eventSubtitle.innerHTML = firstParagraph.innerHTML;
      }
    }
  });
});

onMounted(() => {
  window.addEventListener(
    "hashchange",
    () => {
      if (window.location.hash === "#modal-form") {
        modalForm.openModal();
        window.location.hash = "";
      }
    },
    false,
  );

  addTriggers();
});

const addTriggers = () => {
  const modalFormTriggers = document.querySelectorAll("a[href$='#modal-form']");

  modalFormTriggers.forEach((node) => {
    // Clone & replace node to remove existing event listeners
    const clonedNode = node.cloneNode(true);
    node.parentNode.replaceChild(clonedNode, node);

    clonedNode.addEventListener("click", (event) => {
      // Add event listener
      event.preventDefault();
      modalForm.openModal();
    });
  });
};

onUpdated(() => {
  addTriggers();
});
</script>
